import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useEffect, useState } from 'react';

import { Alerts } from './Alerts';
import { ApiKeys } from './ApiKeys';
import { fetchVersion, type VersionInfo } from './api';
import type { RuntimeConfig } from './config';
import { Fleet } from './Fleet';
import { RemoteAccess } from './RemoteAccess';
import { linkHandler, routePath, useRoute, type Route } from './router';
import { Schedules } from './Schedules';
import { SessionProvider } from './session';
import { useSession } from './sessionContext';
import { AppShell } from './shared/AppShell';
import { EmptyState } from './shared/EmptyState';
import { REMOTE_PERMISSIONS } from './shared/nav';
import { SiteControl } from './SiteControl';
import { webVersion } from './version';

// Charts (Recharts) load only with the telemetry page.
const Telemetry = lazy(() => import('./Telemetry').then((m) => ({ default: m.Telemetry })));

type ApiState =
  | { kind: 'checking' }
  | { kind: 'connected'; info: VersionInfo }
  | { kind: 'unreachable'; error: string };

function useApiVersion(apiUrl: string): ApiState {
  const [api, setApi] = useState<ApiState>({ kind: 'checking' });
  useEffect(() => {
    const controller = new AbortController();
    fetchVersion(apiUrl, controller.signal)
      .then((info) => {
        setApi({ kind: 'connected', info });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setApi({ kind: 'unreachable', error: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      controller.abort();
    };
  }, [apiUrl]);
  return api;
}

/**
 * Signed out (or sign-in not configured): platform status and the sign-in
 * button. Signed in: the app shell with the page for the current route.
 */
export function App({ config }: { config: RuntimeConfig }) {
  const api = useApiVersion(config.apiUrl);
  if (!config.oidc) return <Landing config={config} api={api} />;
  return (
    <SessionProvider apiUrl={config.apiUrl}>
      <SignedInOrLanding config={config} api={api} />
    </SessionProvider>
  );
}

function SignedInOrLanding({ config, api }: { config: RuntimeConfig; api: ApiState }) {
  const session = useSession();
  const route = useRoute();
  if (!session.authenticated) return <Landing config={config} api={api} signIn />;
  const versions = `web ${webVersion()} · api ${api.kind === 'connected' ? api.info.version : '?'}`;
  return (
    <AppShell route={route} environment={config.environment} versions={versions}>
      <Page route={route} />
    </AppShell>
  );
}

/** The page for a route, for the selected tenant. */
function Page({ route }: { route: Route }) {
  const { apiUrl, token, me, membership, permissions, reauthenticate } = useSession();
  if (me.kind === 'idle' || me.kind === 'loading') {
    return <Typography color="text.secondary">Loading…</Typography>;
  }
  if (me.kind === 'error') {
    return <Alert severity="error">Could not load your account: {me.error}</Alert>;
  }
  if (!membership || !token) {
    return (
      <EmptyState title="No tenant yet" testId="no-tenant-page">
        Your account isn&apos;t linked to a tenant. Ask your administrator for access.
      </EmptyState>
    );
  }
  const tenantId = membership.tenant_id;
  switch (route.name) {
    case 'home':
      return (
        <Fleet
          key={tenantId}
          apiUrl={apiUrl}
          tenantId={tenantId}
          token={token}
          canClaim={permissions.includes('appliance.claim')}
          canProvision={permissions.includes('appliance.provision')}
        />
      );
    case 'site':
      return (
        <SiteControl
          key={`${tenantId}/${route.siteId}`}
          apiUrl={apiUrl}
          tenantId={tenantId}
          siteId={route.siteId}
          token={token}
          permissions={permissions}
        />
      );
    case 'schedules':
      return (
        <Schedules
          key={`${tenantId}/${route.siteId}`}
          apiUrl={apiUrl}
          tenantId={tenantId}
          siteId={route.siteId}
          token={token}
          permissions={permissions}
        />
      );
    case 'telemetry':
      return (
        <Suspense fallback={<Typography color="text.secondary">Loading telemetry…</Typography>}>
          <Telemetry
            key={`${tenantId}/${route.siteId}`}
            apiUrl={apiUrl}
            tenantId={tenantId}
            siteId={route.siteId}
            token={token}
            permissions={permissions}
          />
        </Suspense>
      );
    case 'remote':
      if (!REMOTE_PERMISSIONS.some((p) => permissions.includes(p))) return <NotFound />;
      return (
        <RemoteAccess
          key={`${tenantId}/${route.siteId}`}
          apiUrl={apiUrl}
          tenantId={tenantId}
          siteId={route.siteId}
          token={token}
          permissions={permissions}
          platformOperator={me.me.platform_operator}
          onReauthenticate={() => {
            reauthenticate(routePath({ name: 'remote', siteId: route.siteId }));
          }}
        />
      );
    case 'alerts':
      return (
        <Alerts
          key={tenantId}
          apiUrl={apiUrl}
          tenantId={tenantId}
          token={token}
          permissions={permissions}
        />
      );
    case 'apiKeys':
      if (!permissions.includes('api_key.create')) return <NotFound />;
      return (
        <ApiKeys
          key={tenantId}
          apiUrl={apiUrl}
          tenantId={tenantId}
          token={token}
          permissions={permissions}
          siteIds={membership.site_ids}
          userId={me.me.user_id}
          onReauthenticate={() => {
            reauthenticate(routePath({ name: 'apiKeys' }));
          }}
        />
      );
    default:
      return <NotFound />;
  }
}

function NotFound() {
  return (
    <EmptyState
      title="Page not found"
      testId="not-found"
      action={
        <Button variant="contained" href="/" onClick={linkHandler('/')}>
          Go to sites
        </Button>
      }
    >
      This page doesn&apos;t exist, or it isn&apos;t available to you.
    </EmptyState>
  );
}

function StatusRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.5, sm: 2 }}
      sx={{ py: 1.5, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
    >
      <Typography color="text.secondary">{label}</Typography>
      <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
    </Stack>
  );
}

function Landing({
  config,
  api,
  signIn = false,
}: {
  config: RuntimeConfig;
  api: ApiState;
  signIn?: boolean;
}) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6 } }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            IllumiLink
          </Typography>
          {config.environment !== 'production' && (
            <Chip size="small" color="warning" label={config.environment} />
          )}
        </Stack>
        {signIn && <SignInCard />}
        <Typography variant="h5" component="h1" gutterBottom sx={{ mt: signIn ? 4 : 0 }}>
          Platform status
        </Typography>
        <Card>
          <CardContent>
            <StatusRow label="Web app">
              <Typography data-testid="web-version">{webVersion()}</Typography>
            </StatusRow>
            <StatusRow label="API">
              {api.kind === 'checking' && <Chip label="Checking…" size="small" />}
              {api.kind === 'connected' && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Chip label="Connected" color="success" size="small" />
                  <Typography data-testid="api-version">{api.info.version}</Typography>
                </Stack>
              )}
              {api.kind === 'unreachable' && (
                <Stack spacing={0.5}>
                  <Chip
                    label="Unreachable"
                    color="error"
                    size="small"
                    sx={{ alignSelf: 'start' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {api.error}
                  </Typography>
                </Stack>
              )}
            </StatusRow>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

function SignInCard() {
  const session = useSession();
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" component="h2">
            Sign in
          </Typography>
          {session.authError && (
            <Alert severity="error">Sign-in failed: {session.authError.message}</Alert>
          )}
          <Typography color="text.secondary">
            Use your organization&apos;s sign-in, or your IllumiLink account.
          </Typography>
          <Button
            variant="contained"
            data-testid="sign-in"
            disabled={session.authLoading}
            onClick={session.signIn}
            sx={{ alignSelf: 'start' }}
          >
            {session.authLoading ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
